"use client";

import { useEffect, useState, type ReactNode } from "react";
import { X, Filter as FilterIcon } from "lucide-react";
import { ActiveFilterChips } from "./active-filter-chips";
import { FilterPanel } from "./filter-panel";
import { StickySearchBar } from "./sticky-search-bar";
import { useFiltersState } from "./use-filters-state";
import type { FilterGroup } from "./types";

interface FiltersShellProps {
  groups: FilterGroup[];
  /** Right-column content (the product grid/list). */
  children: ReactNode;
  /** Optional sort dropdown rendered next to the search bar. */
  sortControl?: ReactNode;
  /** Optional ad slot rendered as a 3rd sticky column on screens ≥ 1200px. */
  rightAd?: ReactNode;
  /** Search input placeholder copy. */
  searchPlaceholder?: string;
  /** Adds the chip strip above the grid. Default true. */
  showChips?: boolean;
  /** Hide the desktop sidebar entirely (still renders drawer). */
  hideDesktopSidebar?: boolean;
  /** Slot above the filters in both desktop sidebar and mobile drawer.
   *  Useful for page-specific selectors (e.g. "¿Qué quieres comparar?"
   *  basket chips on /comparador/despensa). */
  sidebarTop?: ReactNode;
}

/**
 * The whole filter chrome around a result grid:
 *  - Sticky search bar on top (works at every breakpoint).
 *  - Active chip strip below it.
 *  - Desktop sidebar (lg+) sticky to the left.
 *  - Bottom sheet drawer on mobile/tablet, opened from the search bar.
 *  - Optional sticky 300x600 ad on the right (xl ≥ 1200px).
 */
export function FiltersShell({
  groups,
  children,
  sortControl,
  rightAd,
  searchPlaceholder,
  showChips = true,
  hideDesktopSidebar = false,
  sidebarTop,
}: FiltersShellProps) {
  const { state, set, toggleMulti, removeChip, clearAll } = useFiltersState(groups);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Body scroll-lock while drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const onSetSingle = (param: string, value: string | null) => set(param, value);
  const onSetRange = (param: string, value: [number, number] | null) => set(param, value);

  return (
    <>
      <StickySearchBar
        filterCount={state.count}
        onOpenFilters={() => setDrawerOpen(true)}
        sortControl={sortControl}
        placeholder={searchPlaceholder}
      />

      <div className="grid gap-6 mt-4 lg:grid-cols-[260px_1fr] xl:grid-cols-[280px_1fr_320px] 2xl:grid-cols-[300px_1fr_360px]">
        {!hideDesktopSidebar && (
          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-3">
              {sidebarTop ? (
                <div className="rounded-2xl border bg-card p-4">{sidebarTop}</div>
              ) : null}
              <DesktopSidebar
                groups={groups}
                state={state}
                sortControl={sortControl}
                onToggleMulti={toggleMulti}
                onSetSingle={onSetSingle}
                onSetRange={onSetRange}
                onClearAll={clearAll}
              />
            </div>
          </aside>
        )}

        <main className="min-w-0">
          {showChips && state.chips.length > 0 && (
            <div className="mb-4">
              <ActiveFilterChips
                chips={state.chips}
                onRemove={removeChip}
                onClearAll={clearAll}
              />
            </div>
          )}
          {sortControl && (
            <div className="sm:hidden mb-3 sticky top-29 z-20 bg-background/85 backdrop-blur-md py-1 -mx-1 px-1">
              {sortControl}
            </div>
          )}
          {children}
        </main>

        {rightAd && (
          <aside className="hidden xl:block">
            <div className="sticky top-32">{rightAd}</div>
          </aside>
        )}
      </div>

      {drawerOpen && (
        <MobileDrawer
          groups={groups}
          state={state}
          onToggleMulti={toggleMulti}
          onSetSingle={onSetSingle}
          onSetRange={onSetRange}
          onClearAll={clearAll}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

function DesktopSidebar({
  groups,
  state,
  sortControl,
  onToggleMulti,
  onSetSingle,
  onSetRange,
  onClearAll,
}: {
  groups: FilterGroup[];
  state: ReturnType<typeof useFiltersState>["state"];
  sortControl?: ReactNode;
  onToggleMulti: (param: string, value: string) => void;
  onSetSingle: (param: string, value: string | null) => void;
  onSetRange: (param: string, value: [number, number] | null) => void;
  onClearAll: () => void;
}) {
  return (
    <div className="rounded-2xl border bg-card max-h-[calc(100vh-9rem)] overflow-hidden flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <FilterIcon className="w-4 h-4" />
          Filtros
          {state.count > 0 && (
            <span className="text-[10px] font-bold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5">
              {state.count}
            </span>
          )}
        </h3>
        {state.count > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          >
            Limpiar
          </button>
        )}
      </header>
      {sortControl ? <div className="border-b px-4 py-3">{sortControl}</div> : null}
      <div className="flex-1 overflow-y-auto px-4">
        <FilterPanel
          groups={groups}
          state={state}
          onToggleMulti={onToggleMulti}
          onSetSingle={onSetSingle}
          onSetRange={onSetRange}
        />
      </div>
    </div>
  );
}

function MobileDrawer({
  groups,
  state,
  onToggleMulti,
  onSetSingle,
  onSetRange,
  onClearAll,
  onClose,
}: {
  groups: FilterGroup[];
  state: ReturnType<typeof useFiltersState>["state"];
  onToggleMulti: (param: string, value: string) => void;
  onSetSingle: (param: string, value: string | null) => void;
  onSetRange: (param: string, value: [number, number] | null) => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-80 flex items-end sm:items-center sm:justify-end bg-black/50 backdrop-blur-xs animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full sm:w-96 sm:h-full bg-background sm:rounded-none rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] sm:max-h-none animate-in slide-in-from-bottom sm:slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <h3 className="text-base font-black flex items-center gap-1.5">
            <FilterIcon className="w-4 h-4" />
            Filtros
            {state.count > 0 && (
              <span className="text-[10px] font-bold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5">
                {state.count}
              </span>
            )}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="w-8 h-8 rounded-full border hover:bg-muted flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5">
          <FilterPanel
            groups={groups}
            state={state}
            onToggleMulti={onToggleMulti}
            onSetSingle={onSetSingle}
            onSetRange={onSetRange}
          />
        </div>
        <footer className="px-5 py-3 border-t flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onClearAll}
            disabled={state.count === 0}
            className="flex-1 h-11 rounded-full border bg-card hover:bg-muted text-sm font-semibold disabled:opacity-50"
          >
            Limpiar
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-bold"
          >
            Ver resultados
          </button>
        </footer>
      </div>
    </div>
  );
}
